import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Loader2, Printer, Send } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * WeekenderInvoiceDialog - Extracted from AdminWeekendsTab.js
 * Provides invoice preview and actions:
 * - Invoice preview (HTML rendered)
 * - Print (opens new browser window)
 * - Email to customer
 * 
 * NOTE: All financial data comes from the backend-generated invoice.
 * This component does NOT calculate or modify invoice totals.
 */
export default function WeekenderInvoiceDialog({
  booking,
  open,
  onClose,
  authToken
}) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [invoiceHtml, setInvoiceHtml] = useState('');

  // Fetch invoice when dialog opens
  useEffect(() => {
    const fetchInvoice = async () => {
      if (!booking?.booking_id || !open) return;

      setLoading(true);
      setInvoiceHtml('');

      try {
        const response = await fetch(`${API_URL}/api/weekends/admin/bookings/${booking.booking_id}/invoice`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });

        if (response.ok) {
          const data = await response.json();
          setInvoiceHtml(data.invoice_html);
        } else {
          toast.error('Failed to load invoice');
        }
      } catch (error) {
        console.error('Failed to load invoice:', error);
        toast.error('Failed to load invoice');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, [booking?.booking_id, open, authToken]);

  // Print invoice - opens new browser window
  const printInvoice = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(invoiceHtml);
    printWindow.document.close();
    printWindow.print();
  };

  // Send invoice email
  const sendInvoiceEmail = async () => {
    if (!booking) return;

    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/weekends/admin/bookings/${booking.booking_id}/send-invoice`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'Invoice sent successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send invoice');
      }
    } catch (error) {
      console.error('Failed to send invoice:', error);
      toast.error('Failed to send invoice');
    } finally {
      setSending(false);
    }
  };

  if (!booking) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white text-slate-900">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Invoice</DialogTitle>
          <DialogDescription className="text-slate-600">
            Invoice for booking {booking?.booking_id}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
            </div>
          ) : invoiceHtml ? (
            <div
              className="border rounded-lg overflow-hidden bg-white"
              style={{ maxHeight: '60vh', overflowY: 'auto' }}
              dangerouslySetInnerHTML={{ __html: invoiceHtml }}
              data-testid="invoice-preview"
            />
          ) : (
            <div className="text-center py-12 text-slate-500">
              No invoice data available
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose} data-testid="invoice-close">
            Close
          </Button>
          <Button 
            variant="outline" 
            onClick={printInvoice} 
            disabled={!invoiceHtml || loading}
            data-testid="invoice-print"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
          <Button 
            onClick={sendInvoiceEmail} 
            disabled={sending || !invoiceHtml || loading}
            data-testid="invoice-email"
          >
            {sending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <Send className="w-4 h-4 mr-2" />
            Email to Customer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
